import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Briefcase, Star, Target, Lightbulb } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function SkillsShowcaseGuide() {
  const navigate = useNavigate();

  const hardSkills = [
    "Software & Technology (Excel, coding, design tools)",
    "Languages (spoken or written)",
    "Data Analysis & Reporting",
    "Project Management",
    "Industry-Specific Knowledge",
    "Financial/Budget Management",
    "Writing & Documentation",
    "Technical Certifications"
  ];

  const softSkills = [
    "Leadership & Team Management",
    "Communication (verbal & written)",
    "Problem-Solving & Critical Thinking",
    "Adaptability & Flexibility",
    "Emotional Intelligence",
    "Conflict Resolution",
    "Time Management",
    "Collaboration & Teamwork",
    "Creativity & Innovation",
    "Mentoring & Coaching"
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
          <ArrowLeft className="mr-2 w-4 h-4" /> Back
        </Button>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <Star className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-4xl font-serif font-bold text-secondary-900 mb-2">Skills Showcase</h1>
          <p className="text-lg text-secondary-600">Discover and celebrate your professional strengths</p>
        </div>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Why This Matters</h2>
          <p className="text-secondary-700 mb-4">
            Women often underestimate their skills and accomplishments. This exercise helps you see your true professional value, builds confidence for interviews and reviews, and prepares you to articulate your worth.
          </p>
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <p className="text-blue-800 font-medium">
              💡 Studies show women apply for jobs only when they meet 100% of qualifications, while men apply at 60%. Knowing your skills changes this equation.
            </p>
          </div>
        </section>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Briefcase className="w-6 h-6 text-blue-500" />
            Hard Skills to Consider
          </h2>
          <p className="text-secondary-600 mb-4">Technical abilities you've developed through training and experience:</p>
          <div className="grid md:grid-cols-2 gap-3">
            {hardSkills.map((skill, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                <div className="w-2 h-2 bg-blue-500 rounded-full" />
                <span className="text-secondary-700">{skill}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Target className="w-6 h-6 text-purple-500" />
            Soft Skills to Consider
          </h2>
          <p className="text-secondary-600 mb-4">Interpersonal abilities that make you effective:</p>
          <div className="grid md:grid-cols-2 gap-3">
            {softSkills.map((skill, i) => (
              <div key={i} className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                <div className="w-2 h-2 bg-purple-500 rounded-full" />
                <span className="text-secondary-700">{skill}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 border-2 border-blue-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Lightbulb className="w-6 h-6 text-amber-500" />
            Today's Exercise
          </h2>
          <ol className="space-y-4">
            <li className="flex items-start gap-3">
              <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">1</span>
              <div>
                <p className="font-medium text-secondary-900">List your top 5 hard skills</p>
                <p className="text-secondary-600 text-sm">What technical abilities set you apart?</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">2</span>
              <div>
                <p className="font-medium text-secondary-900">List your top 5 soft skills</p>
                <p className="text-secondary-600 text-sm">What interpersonal strengths do you bring?</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">3</span>
              <div>
                <p className="font-medium text-secondary-900">Ask for feedback</p>
                <p className="text-secondary-600 text-sm">Message a trusted colleague: "What do you see as my top 3 professional strengths?"</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shrink-0">4</span>
              <div>
                <p className="font-medium text-secondary-900">Create your "brag document"</p>
                <p className="text-secondary-600 text-sm">Keep a running list of accomplishments, feedback, and wins</p>
              </div>
            </li>
          </ol>
        </section>

        <section className="bg-amber-50 rounded-2xl p-6 border border-amber-200">
          <h3 className="font-bold text-amber-800 mb-2">📝 Journal Prompt</h3>
          <p className="text-amber-900">
            "Looking at my skills list, which ones have I been undervaluing? How would I talk about these skills if I were advocating for my best friend instead of myself?"
          </p>
        </section>
      </main>
    </div>
  );
}

export default SkillsShowcaseGuide;
